import { useState } from "react";
import { toast } from "sonner";
import { Download, UploadCloud, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, can, download, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KINDS = { vi: "VI Statement", dse: "DSE Transactions", fl: "FL Transactions", party: "Creditors & Debtors (Party Master + Opening)" };
const ST = { ok: "bg-emerald-50 text-emerald-700 border-emerald-200", error: "bg-red-50 text-red-700 border-red-200", duplicate: "bg-orange-50 text-orange-700 border-orange-200" };

export function Preview({ batch, onConfirm, busy, result }) {
  const { user } = useAuth();
  const admin = can(user, "import:confirm");
  return (
    <div className="space-y-4 fade-up" data-testid="import-preview">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {["ok", "duplicate", "error"].map((s) => (
          <div key={s} className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <div className="text-[11px] uppercase text-slate-500">{s === "ok" ? "Valid rows" : s === "duplicate" ? "Duplicates (rejected)" : "Errors"}</div>
            <div className="num text-xl font-bold" data-testid={`import-count-${s}`}>{batch.counts[s] || 0}</div>
          </div>
        ))}
        {Object.entries(batch.totals).map(([k, v]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <div className="text-[11px] uppercase text-slate-500">Total {k.replace("_", " ")}</div>
            <div className="num text-lg font-bold" data-testid={`import-total-${k}`}>{inr(v)}</div>
          </div>
        ))}
      </div>
      <div className="overflow-auto max-h-[50vh] border border-slate-200 rounded-xl bg-white">
        <table className="dense-table" data-testid="import-preview-table">
          <thead><tr><th>Row</th><th>Status</th>{batch.columns.map((c) => <th key={c}>{c}</th>)}<th>Errors / Notes</th></tr></thead>
          <tbody>
            {batch.rows.map((r) => (
              <tr key={r.row}>
                <td className="num">{r.row}</td>
                <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${ST[r.status]}`}>{r.status}</span></td>
                {batch.columns.map((c) => <td key={c} className="num">{r.data[c]}</td>)}
                <td className="text-red-700 text-xs whitespace-normal min-w-[220px]">{r.errors.join("; ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm" data-testid="import-result">
          <CheckCircle2 className="h-4 w-4 inline text-emerald-700" /> Created {result.created} entries, failed {result.failed}.
          {result.results.filter((x) => !x.ok).map((x, i) => <div key={i} className="text-red-700 text-xs mt-1">Row {x.row} ({x.kind}): {x.error}</div>)}
        </div>
      ) : admin ? (
        <Button onClick={onConfirm} disabled={busy || !batch.counts.ok} className="bg-[#E60000] hover:bg-[#c40000]" data-testid="import-confirm-btn">
          {busy ? "Saving..." : `Confirm & save ${batch.counts.ok || 0} valid rows`}
        </Button>
      ) : (
        <p className="text-sm text-orange-700">Preview ready. Entries save karne ke liye Admin confirmation zaroori hai.</p>
      )}
    </div>
  );
}

export default function ImportPage() {
  const { bump } = useAuth();
  const [kind, setKind] = useState("vi");
  const [file, setFile] = useState(null);
  const [batch, setBatch] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    if (!file) return toast.error("Select a file");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post("/import/preview", fd);
      setBatch(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/import/confirm/${batch.id}`);
      setResult(data);
      bump();
      toast.success(`Imported ${data.created} entries`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Excel Import</h1>
        <span className="hint-tag mt-2">Template download karein → fill karein → preview & validation → Admin confirm ke baad hi save</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[11px] text-slate-500 mb-1">Import type</div>
          <Select value={kind} onValueChange={(v) => { setKind(v); setBatch(null); setResult(null); }}>
            <SelectTrigger className="w-[200px]" data-testid="import-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={() => download(`/import/template/${kind}`, {}, `ayzal_${kind}_import_template.xlsx`).catch((e) => toast.error(errMsg(e)))} data-testid="import-template-btn">
          <Download className="h-4 w-4" /> Sample template
        </Button>
        <Input type="file" accept=".xlsx,.csv" className="w-[260px]" onChange={(e) => { setFile(e.target.files?.[0] || null); setBatch(null); setResult(null); }} data-testid="import-file-input" />
        <Button onClick={preview} disabled={busy} data-testid="import-preview-btn"><UploadCloud className="h-4 w-4" /> Validate & preview</Button>
      </div>
      {batch && <Preview batch={batch} onConfirm={confirm} busy={busy} result={result} />}
    </div>
  );
}
